import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import type { UserDto } from '@/app/common/types/user.ts';
import { useLazyGetMeQuery } from '@/app/store/api/userApi.ts';
import { baseApi } from '@/app/store/api/baseApi.ts';
import { store } from '@/app/store/store.ts';
import { setErrorContext } from '@/app/common/utils/errorReporter.ts';
import tracker from '@/app/common/utils/openreplay.ts';

export interface AuthContextValue {
  isLoggedIn: boolean | null;
  token: string | null;
  user: UserDto | null;
  login: (token: string, user: UserDto) => void;
  loginWithToken: (token: string) => Promise<UserDto>;
  logout: () => void;
  setUser: (user: UserDto) => void;
}

export const AuthContext = createContext<AuthContextValue>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserDto | null>(null);
  const [triggerGetMe] = useLazyGetMeQuery();

  const login = useCallback((newToken: string, newUser: UserDto) => {
    localStorage.setItem('HlidkainsolvenceToken', newToken);
    setToken(newToken);
    setUser(newUser);
    setIsLoggedIn(true);
    setErrorContext({ userName: newUser.userName });
    tracker.setUserID(newUser.userName);
  }, []);

  const loginWithToken = useCallback(async (newToken: string): Promise<UserDto> => {
    localStorage.setItem('HlidkainsolvenceToken', newToken);
    try {
      const me = await triggerGetMe(undefined, false).unwrap();
      setToken(newToken);
      setUser(me);
      setIsLoggedIn(true);
      setErrorContext({ userName: me.userName });
      tracker.setUserID(me.userName);
      return me;
    } catch (err) {
      localStorage.removeItem('HlidkainsolvenceToken');
      throw err;
    }
  }, [triggerGetMe]);

  const logout = useCallback(() => {
    localStorage.removeItem('HlidkainsolvenceToken');
    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
    setErrorContext({ userName: null, accountId: null });
    // Clear RTK Query cache so the next login doesn't use stale data from the previous session
    store.dispatch(baseApi.util.resetApiState());
  }, []);

  useEffect(() => {
    const init = async () => {
      const storedToken = localStorage.getItem('HlidkainsolvenceToken');
      if (storedToken) {
        try {
          const me = await triggerGetMe().unwrap();
          // Only update state if the token hasn't been replaced by another login (e.g. loginWithToken)
          if (localStorage.getItem('HlidkainsolvenceToken') === storedToken) {
            setToken(storedToken);
            setUser(me);
            setIsLoggedIn(true);
            setErrorContext({ userName: me.userName });
            tracker.setUserID(me.userName);
          }
        } catch {
          // Only clear if the token hasn't been replaced by another login
          if (localStorage.getItem('HlidkainsolvenceToken') === storedToken) {
            localStorage.removeItem('HlidkainsolvenceToken');
            setIsLoggedIn(false);
          }
        }
      } else {
        setIsLoggedIn(false);
      }
    };

    init();
  }, [triggerGetMe]);

  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, token, user, login, loginWithToken, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}
